import { css } from '@compiled/react';
import { useIntl } from 'react-intl';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { TemplateType } from '@/content/templates/template.types';
import { wacToken } from '@/libraries/design/foundations/index.ts';
import { ZoomIn } from '@/libraries/wpl/foundations/src/icons/content/zoom-in';
import { ZoomOut } from '@/libraries/wpl/foundations/src/icons/content/zoom-out';

const previewTopBar = css({
	display: 'flex',
	padding: `${token('space.075')} ${token('space.150')}`,
	height: token('space.200'),
	boxSizing: 'border-box',
	borderStartStartRadius: token('space.050'),
	borderStartEndRadius: token('space.050'),
	backgroundColor: wacToken('color.extended.blue1000'),
});
const previewBarDot = css({
	marginInlineEnd: token('space.050'),
	width: 5,
	height: 5,
	backgroundColor: wacToken('color.neutral.light0'),
	borderRadius: '50%',
});

const previewImage = css({
	width: '100%',
	maxWidth: '100%',
});

export interface UniformTemplatePreviewWindowProps {
	url: string;
	alt: string;
	templateType?: TemplateType;
}

export const UniformTemplatePreviewWindow = ({
	url,
	alt,
	templateType,
}: UniformTemplatePreviewWindowProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const templatePreview = css({
		maxWidth: '100%',
		margin: '0 auto',
	});

	const previewScreenshot = css({
		height: 360,
		overflowY: 'auto',
		backgroundColor: wacToken('color.neutral.light0'),
		borderEndStartRadius: token('space.050'),
		borderEndEndRadius: token('space.050'),
		border: `1px solid ${wacToken('color.neutral.light300')}`,
		[media.only.xs]: {
			height: '198px',
			overflowY: 'hidden',
		},
	});

	const whiteboardPreviewWrapper = css({
		cursor: 'grab',

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.react-transform-wrapper': {
			height: '100%',
		},
	});

	const zoomControls = css({
		display: 'flex',
		gap: token('space.100'),
		position: 'absolute',
		insetBlockEnd: token('space.200'),
		insetInlineEnd: token('space.200'),
	});

	const zoomControl = css({
		padding: 0,
		border: 0,
		background: 'none',
		cursor: 'pointer',
	});

	const regionLabel = intl.formatMessage({
		id: 'template-preview.scrollable-content',
		defaultMessage: 'Scrollbar',
	});

	return (
		<div css={templatePreview}>
			{templateType === TemplateType.Whiteboard ? (
				<>
					<div css={previewTopBar}>
						<div css={previewBarDot} />
						<div css={previewBarDot} />
						<div css={previewBarDot} />
					</div>
					<div
						css={[previewScreenshot, whiteboardPreviewWrapper]}
						role="region"
						aria-label={regionLabel}
					>
						<TransformWrapper>
							{({ zoomIn, zoomOut }) => (
								<>
									<TransformComponent>
										{/* eslint-disable-next-line no-restricted-syntax */}
										<img css={previewImage} src={url} alt={alt} />
									</TransformComponent>
									<div css={zoomControls}>
										<button
											type="button"
											css={zoomControl}
											onClick={() => {
												zoomIn();
												createAnalyticsEvent({
													eventType: 'ui',
													action: 'clicked',
													actionSubject: 'button',
													actionSubjectId: 'zoomIn',
													source: 'templatePreview',
												}).fire();
											}}
											aria-label={intl.formatMessage({
												id: 'template-preview.zoom-in',
												defaultMessage: 'Zoom In',
											})}
										>
											<ZoomIn size="16" />
										</button>
										<button
											type="button"
											css={zoomControl}
											onClick={() => {
												zoomOut();
												createAnalyticsEvent({
													eventType: 'ui',
													action: 'clicked',
													actionSubject: 'button',
													actionSubjectId: 'zoomOut',
													source: 'templatePreview',
												}).fire();
											}}
											aria-label={intl.formatMessage({
												id: 'template-preview.zoom-out',
												defaultMessage: 'Zoom Out',
											})}
										>
											<ZoomOut size="16" />
										</button>
									</div>
								</>
							)}
						</TransformWrapper>
					</div>
				</>
			) : (
				<>
					<div css={previewTopBar}>
						<div css={previewBarDot} />
						<div css={previewBarDot} />
						<div css={previewBarDot} />
					</div>
					<div css={previewScreenshot} role="region" aria-label={regionLabel}>
						{/* eslint-disable-next-line no-restricted-syntax */}
						<img css={previewImage} src={url} alt={alt} />
					</div>
				</>
			)}
		</div>
	);
};
